import { assignStructuredSearch } from '@classic/Foundation/Analytics/Analytics'
import { StructuredSearchData } from '@classic/Foundation/Analytics/AnalyticsDataLayer'
import type { RaceCodesLowerCase } from '@core/Areas/Racing/Types/MeetingInformation'
import { RangeBoundaries } from '@core/Areas/StructuredSearch/constants'
import type { StructuredSearchFields } from '@core/Areas/StructuredSearch/types'

const enum LocalConstants {
  contestTypes = 'contestTypes',
  eventDates = 'eventDates',
  barrierNumbers = 'barrierNumbers',
  boxNumbers = 'boxNumbers',
  oneToFour = 'oneToFour',
  oneToFive = 'oneToFive',
  fivePlus = 'fivePlus',
  sixPlus = 'sixPlus',
  weightChange = 'weightChange',
  distanceChange = 'distanceChange',
  up = 'up',
  down = 'down',
  same = 'same',
  today = 'today',
  tomorrow = 'tomorrow',
  beyond = 'beyond',
  timezoneOffset = 'timeZoneOffset',
  winTrackConditions = 'winTrackConditions',
  winTrackFirm = 'firm',
  winTrackGood = 'good',
  winTrackHeavy = 'heavy',
  winTrackSoft = 'soft',
  winTrackSynthetic = 'synthetic',
  locationGroups = 'locationGroups',
  locationGroupAustralia = 'AUS',
  locationGroupNewZealand = 'NZ',
  locationGroupInternational = 'INT',
  trackId = 'trackIds',
}

/** Returns search query params for Smart Search API call, ie `'?params' | ''` */
export function buildStructuredSearchUrl(parameters: StructuredSearchFields): string {
  const queryTokens: string[] = []
  let urlString = ''

  const structuredSearchData: StructuredSearchData = { criteria: null }
  structuredSearchData.criteria = {}

  const offset: number = new Date().getTimezoneOffset() / -60

  queryTokens.push(buildQueryToken(LocalConstants.timezoneOffset, offset))

  if (parameters) {
    if (!(parameters.contestTypeRace && parameters.contestTypeTrot && parameters.contestTypeDogs)) {
      const contestTypeArray: RaceCodesLowerCase[] = []

      if (parameters.contestTypeRace) {
        queryTokens.push(buildQueryToken(LocalConstants.contestTypes, 'races'))
        contestTypeArray.push('races')
      }

      if (parameters.contestTypeTrot) {
        queryTokens.push(buildQueryToken(LocalConstants.contestTypes, 'trots'))
        contestTypeArray.push('trots')
      }

      if (parameters.contestTypeDogs) {
        queryTokens.push(buildQueryToken(LocalConstants.contestTypes, 'dogs'))
        contestTypeArray.push('dogs')
      }

      if (contestTypeArray.length > 0) {
        structuredSearchData.criteria.contestType = contestTypeArray
      }
    }

    if (!(parameters.dateToday && parameters.dateTomorrow && parameters.dateBeyondTomorrow)) {
      const dateArray = []
      if (parameters.dateToday) {
        queryTokens.push(buildQueryToken(LocalConstants.eventDates, LocalConstants.today))
        dateArray.push(LocalConstants.today)
      }
      if (parameters.dateTomorrow) {
        queryTokens.push(buildQueryToken(LocalConstants.eventDates, LocalConstants.tomorrow))
        dateArray.push(LocalConstants.tomorrow)
      }
      if (parameters.dateBeyondTomorrow) {
        queryTokens.push(buildQueryToken(LocalConstants.eventDates, LocalConstants.beyond))
        dateArray.push(LocalConstants.beyond)
      }

      if (dateArray.length > 0) {
        structuredSearchData.criteria.date = dateArray
      }
    }

    if (parameters.firstUp) {
      queryTokens.push(buildQueryToken('spellFirstUp', true))
      structuredSearchData.criteria.firstUp = true
    }

    if (parameters.secondUp) {
      queryTokens.push(buildQueryToken('spellSecondUp', true))
      structuredSearchData.criteria.secondUp = true
    }

    if (parameters.gearChange) {
      queryTokens.push(buildQueryToken('gearChange', true))
      structuredSearchData.criteria.gearChange = true
    }

    if (parameters.wonLastStart) {
      queryTokens.push(buildQueryToken('lastStartWin', true))
      structuredSearchData.criteria.wonLastStart = true
    }

    if (parameters.placedLastStart) {
      queryTokens.push(buildQueryToken('lastStartPlace', true))
      structuredSearchData.criteria.placedLastStart = true
    }

    if (parameters.careerWinPercentage) {
      queryTokens.push(buildQueryToken('winPercentageCareer', parameters.careerWinPercentage))
      structuredSearchData.criteria.careerWinPercentage = parameters.careerWinPercentage
    }

    if (parameters.trackWinPercentage) {
      queryTokens.push(buildQueryToken('winPercentageTrack', parameters.trackWinPercentage))
      structuredSearchData.criteria.trackWinPercentage = parameters.trackWinPercentage
    }

    if (parameters.distanceWinPercentage) {
      queryTokens.push(buildQueryToken('winPercentageDistance', parameters.distanceWinPercentage))
      structuredSearchData.criteria.distanceWinPercentage = parameters.distanceWinPercentage
    }

    if (parameters.trackdistanceWinPercentage) {
      queryTokens.push(
        buildQueryToken('winPercentageDistanceTrack', parameters.trackdistanceWinPercentage)
      )
      structuredSearchData.criteria.trackdistanceWinPercentage =
        parameters.trackdistanceWinPercentage
    }

    if (!(parameters.barrierNumberOneToFive && parameters.barrierNumberSixPlus)) {
      const barrierArray = []
      if (parameters.barrierNumberOneToFive) {
        queryTokens.push(buildQueryToken(LocalConstants.barrierNumbers, LocalConstants.oneToFive))
        barrierArray.push(LocalConstants.oneToFive)
      }

      if (parameters.barrierNumberSixPlus) {
        queryTokens.push(buildQueryToken(LocalConstants.barrierNumbers, LocalConstants.sixPlus))
        barrierArray.push(LocalConstants.sixPlus)
      }
      if (barrierArray.length > 0) {
        structuredSearchData.criteria.barrierNumber = barrierArray
      }
    }

    if (!(parameters.boxNumberOneToFour && parameters.boxNumberFivePlus)) {
      const boxArray = []
      if (parameters.boxNumberOneToFour) {
        queryTokens.push(buildQueryToken(LocalConstants.boxNumbers, LocalConstants.oneToFour))
        boxArray.push(LocalConstants.oneToFour)
      }

      if (parameters.boxNumberFivePlus) {
        queryTokens.push(buildQueryToken(LocalConstants.boxNumbers, LocalConstants.fivePlus))
        boxArray.push(LocalConstants.fivePlus)
      }
      if (boxArray.length > 0) {
        structuredSearchData.criteria.boxNumber = boxArray
      }
    }

    if (parameters.apprentice) {
      queryTokens.push(buildQueryToken('apprentice', true))
      structuredSearchData.criteria.apprentice = true
    }

    if (parameters.favouriteLastStart) {
      queryTokens.push(buildQueryToken('lastStartFavourite', true))
      structuredSearchData.criteria.favouriteLastStart = true
    }

    if (
      !(parameters.weightChangedUp && parameters.weightChangedDown && parameters.weightNotChanged)
    ) {
      const weightChangedArray = []
      if (parameters.weightChangedUp) {
        queryTokens.push(buildQueryToken(LocalConstants.weightChange, LocalConstants.up))
        weightChangedArray.push(LocalConstants.up)
      }

      if (parameters.weightChangedDown) {
        queryTokens.push(buildQueryToken(LocalConstants.weightChange, LocalConstants.down))
        weightChangedArray.push(LocalConstants.down)
      }

      if (parameters.weightNotChanged) {
        queryTokens.push(buildQueryToken(LocalConstants.weightChange, LocalConstants.same))
        weightChangedArray.push(LocalConstants.same)
      }
      if (weightChangedArray.length > 0) {
        structuredSearchData.criteria.weightChanged = weightChangedArray
      }
    }

    if (
      !(
        parameters.distanceChangedUp &&
        parameters.distanceChangedDown &&
        parameters.distanceNotChanged
      )
    ) {
      const distanceChangedArray = []
      if (parameters.distanceChangedUp) {
        queryTokens.push(buildQueryToken(LocalConstants.distanceChange, LocalConstants.up))
        distanceChangedArray.push(LocalConstants.up)
      }

      if (parameters.distanceChangedDown) {
        queryTokens.push(buildQueryToken(LocalConstants.distanceChange, LocalConstants.down))
        distanceChangedArray.push(LocalConstants.down)
      }

      if (parameters.distanceNotChanged) {
        queryTokens.push(buildQueryToken(LocalConstants.distanceChange, LocalConstants.same))
        distanceChangedArray.push(LocalConstants.same)
      }

      if (distanceChangedArray.length > 0) {
        structuredSearchData.criteria.distanceChanged = distanceChangedArray
      }
    }

    if (
      parameters.minDaysSinceLastWin > RangeBoundaries.minDaysSinceLastWin ||
      parameters.maxDaysSinceLastWin < RangeBoundaries.maxDaysSinceLastWin
    ) {
      structuredSearchData.criteria.daysSinceLastWin = {}
      if (parameters.minDaysSinceLastWin > RangeBoundaries.minDaysSinceLastWin) {
        queryTokens.push(buildQueryToken('daysSinceLastWinMin', parameters.minDaysSinceLastWin))
        structuredSearchData.criteria.daysSinceLastWin.min = parameters.minDaysSinceLastWin
      }

      if (parameters.maxDaysSinceLastWin < RangeBoundaries.maxDaysSinceLastWin) {
        queryTokens.push(buildQueryToken('daysSinceLastWinMax', parameters.maxDaysSinceLastWin))
        structuredSearchData.criteria.daysSinceLastWin.max = parameters.maxDaysSinceLastWin
      }
    }

    if (
      parameters.minDaysSinceLastStart > RangeBoundaries.minDaysSinceLastStart ||
      parameters.maxDaysSinceLastStart < RangeBoundaries.maxDaysSinceLastStart
    ) {
      structuredSearchData.criteria.daysSinceLastStart = {}
      if (parameters.minDaysSinceLastStart > RangeBoundaries.minDaysSinceLastStart) {
        queryTokens.push(buildQueryToken('daysSinceLastStartMin', parameters.minDaysSinceLastStart))
        structuredSearchData.criteria.daysSinceLastStart.min = parameters.minDaysSinceLastStart
      }

      if (parameters.maxDaysSinceLastStart < RangeBoundaries.maxDaysSinceLastStart) {
        queryTokens.push(buildQueryToken('daysSinceLastStartMax', parameters.maxDaysSinceLastStart))
        structuredSearchData.criteria.daysSinceLastStart.max = parameters.maxDaysSinceLastStart
      }
    }

    if (
      parameters.minStartsSinceLastSpell > RangeBoundaries.minStartsSinceLastSpell ||
      parameters.maxStartsSinceLastSpell < RangeBoundaries.maxStartsSinceLastSpell
    ) {
      structuredSearchData.criteria.startsSinceLastSpell = {}
      if (parameters.minStartsSinceLastSpell > RangeBoundaries.minStartsSinceLastSpell) {
        queryTokens.push(
          buildQueryToken('startsSinceLastSpellMin', parameters.minStartsSinceLastSpell)
        )
        structuredSearchData.criteria.startsSinceLastSpell.min = parameters.minStartsSinceLastSpell
      }

      if (parameters.maxStartsSinceLastSpell < RangeBoundaries.maxStartsSinceLastSpell) {
        queryTokens.push(
          buildQueryToken('startsSinceLastSpellMax', parameters.maxStartsSinceLastSpell)
        )
        structuredSearchData.criteria.startsSinceLastSpell.max = parameters.maxStartsSinceLastSpell
      }
    }

    if (
      parameters.minFixedOddsPrice > RangeBoundaries.minFixedOddsPrice ||
      parameters.maxFixedOddsPrice < RangeBoundaries.maxFixedOddsPrice
    ) {
      structuredSearchData.criteria.fixedOddsPrice = {}
      if (parameters.minFixedOddsPrice > RangeBoundaries.minFixedOddsPrice) {
        queryTokens.push(buildQueryToken('fixedOddsPriceMin', parameters.minFixedOddsPrice))
        structuredSearchData.criteria.fixedOddsPrice.min = parameters.minFixedOddsPrice
      }

      if (parameters.maxFixedOddsPrice < RangeBoundaries.maxFixedOddsPrice) {
        queryTokens.push(buildQueryToken('fixedOddsPriceMax', parameters.maxFixedOddsPrice))
        structuredSearchData.criteria.fixedOddsPrice.max = parameters.maxFixedOddsPrice
      }
    }

    if (
      !(
        parameters.winTrackConditionFirm &&
        parameters.winTrackConditionGood &&
        parameters.winTrackConditionHeavy &&
        parameters.winTrackConditionSoft &&
        parameters.winTrackConditionSynthetic
      )
    ) {
      const winTrackConditionsArray = []

      if (parameters.winTrackConditionFirm) {
        queryTokens.push(
          buildQueryToken(LocalConstants.winTrackConditions, LocalConstants.winTrackFirm)
        )
        winTrackConditionsArray.push(LocalConstants.winTrackFirm)
      }

      if (parameters.winTrackConditionGood) {
        queryTokens.push(
          buildQueryToken(LocalConstants.winTrackConditions, LocalConstants.winTrackGood)
        )
        winTrackConditionsArray.push(LocalConstants.winTrackGood)
      }

      if (parameters.winTrackConditionSoft) {
        queryTokens.push(
          buildQueryToken(LocalConstants.winTrackConditions, LocalConstants.winTrackSoft)
        )
        winTrackConditionsArray.push(LocalConstants.winTrackSoft)
      }

      if (parameters.winTrackConditionHeavy) {
        queryTokens.push(
          buildQueryToken(LocalConstants.winTrackConditions, LocalConstants.winTrackHeavy)
        )
        winTrackConditionsArray.push(LocalConstants.winTrackHeavy)
      }

      if (parameters.winTrackConditionSynthetic) {
        queryTokens.push(
          buildQueryToken(LocalConstants.winTrackConditions, LocalConstants.winTrackSynthetic)
        )
        winTrackConditionsArray.push(LocalConstants.winTrackSynthetic)
      }

      if (winTrackConditionsArray.length > 0) {
        structuredSearchData.criteria.winTrackConditions = winTrackConditionsArray
      }
    }

    if (
      !(
        parameters.locationCountryAustralia &&
        parameters.locationCountryInternational &&
        parameters.locationCountryNewZealand
      )
    ) {
      const locationGroup = []
      if (parameters.locationCountryAustralia) {
        queryTokens.push(
          buildQueryToken(LocalConstants.locationGroups, LocalConstants.locationGroupAustralia)
        )
        locationGroup.push(LocalConstants.locationGroupAustralia)
      }
      if (parameters.locationCountryNewZealand) {
        queryTokens.push(
          buildQueryToken(LocalConstants.locationGroups, LocalConstants.locationGroupNewZealand)
        )
        locationGroup.push(LocalConstants.locationGroupNewZealand)
      }
      if (parameters.locationCountryInternational) {
        queryTokens.push(
          buildQueryToken(LocalConstants.locationGroups, LocalConstants.locationGroupInternational)
        )
        locationGroup.push(LocalConstants.locationGroupInternational)
      }

      if (locationGroup.length > 0) {
        structuredSearchData.criteria.location = { locationGroup, trackIds: null }
      }
    }

    if (parameters.selectedTracks && parameters.selectedTracks.length > 0) {
      parameters.selectedTracks.forEach(trackId =>
        queryTokens.push(buildQueryToken(LocalConstants.trackId, trackId))
      )

      if (structuredSearchData.criteria.location) {
        structuredSearchData.criteria.location.trackIds = parameters.selectedTracks
      } else {
        structuredSearchData.criteria.location = {
          locationGroup: null,
          trackIds: parameters.selectedTracks,
        }
      }
    }
  }

  if (
    Object.keys(structuredSearchData.criteria).length === 0 &&
    structuredSearchData.criteria.constructor === Object
  ) {
    structuredSearchData.criteria = null
  }

  if (queryTokens.length > 0) {
    urlString = queryTokens.join('&')
  }

  assignStructuredSearch(structuredSearchData)

  return urlString ? `?${urlString}` : ''
}

// =============
// Local Helpers
// =============

function buildQueryToken(fieldName: string, value: string | number | boolean): string {
  return `${fieldName}=${encodeURIComponent(value)}`
}

// cspell: ignore trackdistance
